import {
  Button,
  createTheme,
  type DefaultMantineColor,
  defaultVariantColorsResolver,
  type MantineColorsTuple,
  NumberInput,
  parseThemeColor,
  rem,
  SegmentedControl,
  Select,
  TextInput,
  type VariantColorsResolver,
} from '@mantine/core';

type ThemeColors = 'brand' | DefaultMantineColor;

declare module '@mantine/core' {
  export interface MantineThemeColorsOverride {
    colors: Record<ThemeColors, MantineColorsTuple>;
  }
}

const variantColorResolver: VariantColorsResolver = (input) => {
  const defaultResolvedColors = defaultVariantColorsResolver(input);
  const parsedColor = parseThemeColor({
    color: input.color || input.theme.primaryColor,
    theme: input.theme,
  });

  // Custom handling for dark buttons with filled variant
  if (
    parsedColor.isThemeColor &&
    parsedColor.color === 'dark' &&
    input.variant === 'filled'
  ) {
    const shadeIndex = parsedColor.shade || 7;
    const hoverShadeIndex = Math.max(0, shadeIndex - 1);

    return {
      ...defaultResolvedColors,
      background: `var(--mantine-color-dark-${shadeIndex})`,
      hover: `var(--mantine-color-dark-${hoverShadeIndex})`,
      border: 'none',
      color: 'var(--mantine-color-white)',
    };
  }

  return defaultResolvedColors;
};

export const brandTheme = createTheme({
  defaultRadius: 'md',
  cursorType: 'pointer',
  primaryColor: 'brand',
  primaryShade: 5,
  black: '#0F0F12',
  white: '#FFFFFF',
  variantColorResolver,
  components: {
    Button: Button.extend({
      styles: (_, { size, styles }) => ({
        root: {
          ...(size === 'lg' && {
            fontSize: rem(14),
            height: rem(40),
          }),
        },
        ...styles,
      }),
    }),
    NumberInput: NumberInput.extend({
      styles: (theme) => ({
        input: {
          backgroundColor: theme.colors.dark[8],
          height: rem(40),
        },
        label: {
          fontSize: theme.fontSizes.xs,
          fontWeight: 500,
        },
      }),
      defaultProps: {
        variant: 'filled',
      },
    }),
    TextInput: TextInput.extend({
      styles: (theme) => ({
        input: {
          backgroundColor: theme.colors.dark[8],
          height: rem(40),
        },
        label: {
          fontSize: theme.fontSizes.xs,
          fontWeight: 500,
        },
      }),
      defaultProps: {
        variant: 'filled',
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      styles: (theme) => ({
        root: {
          backgroundColor: theme.colors.dark[8],
          paddingTop: 4.5,
          paddingLeft: 4,
          paddingRight: 4,
          paddingBottom: 4.5,
          minHeight: 40,
          height: 40,
        },
        label: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        indicator: {
          border: `2px solid ${theme.colors.brand[5]}`,
          backgroundColor: theme.colors.dark[7],
        },
      }),
    }),
    Select: Select.extend({
      styles: (theme) => ({
        input: {
          backgroundColor: theme.colors.dark[8],
          height: rem(40),
        },
        label: {
          fontSize: theme.fontSizes.xs,
          fontWeight: 500,
        },
      }),
      defaultProps: {
        variant: 'filled',
      },
    }),
  },
  colors: {
    brand: [
      '#F5F3FF',
      '#EDE9FE',
      '#DDD6FE',
      '#C4B5FD',
      '#A78BFA',
      '#8B5CF6',
      '#7C3AED',
      '#6D28D9',
      '#5B21B6',
      '#4C1D95',
    ],
    dark: [
      '#F6F6F9',
      '#EDEDF1',
      '#D7D8E0',
      '#62627B',
      '#3A3A49',
      '#31313E',
      '#292933',
      '#202028',
      '#18181E',
      '#0F0F12',
    ],
    orange: [
      '#FFF7ED',
      '#FFEDD5',
      '#FED7AA',
      '#FDBA74',
      '#FB923C',
      '#F97316',
      '#EA580C',
      '#C2410C',
      '#9A3412',
      '#7C2D12',
    ],
    lime: [
      '#F7FEE7',
      '#ECFCCB',
      '#D9F99D',
      '#BEF264',
      '#A3E635',
      '#84CC16',
      '#65A30D',
      '#4D7C0F',
      '#4D7C0F',
      '#365314',
    ],
  },
});
